/**
 * VetDiscountBanner — ID.me veteran verification + 10% discount affordance.
 *
 * Purpose: Surface the veteran discount on Cart, Checkout, and (compact variant)
 *   on ProductDetail near the price. Three states driven by IDME_ENABLED flag and
 *   the current session role:
 *
 *   1. Verified   — session role === 'vet_app_verified': green confirmation pill.
 *   2. Live CTA   — IDME_ENABLED true + not yet verified: clickable CTA linking to
 *                   veterans.pro/onboarding/verify/idme (with returnTo).
 *   3. Hidden     — IDME_ENABLED false: renders nothing. A "coming soon" pill on
 *                   cart/checkout advertised a 10% discount that could not be
 *                   obtained and invited buyers to abandon and come back later.
 *
 * Inputs:
 *   compact (boolean, optional) — renders a single-line inline note for ProductDetail.
 * Outputs: JSX | null (null when session is loading to avoid flash).
 * Constraints:
 *   - Never renders a broken link, or an unobtainable perk, when IDME_ENABLED is false.
 *   - Uses useAuth() from src/lib/auth-client (TanStack Query-backed, cookie session).
 *   - Role 'vet_app_verified' is the Tier-2 ID.me role per @veterans/types TIER_2_IDME_ROLE.
 *   - Accessible: all interactive elements have aria labels; status states use role="status".
 *
 * SPORT: veterans/shop/src/components/vite/VetDiscountBanner.tsx
 */

import { useAuth } from '@/lib/auth-client'
import { IDME_ENABLED } from '@/lib/idme-flag'

/** Tier-2 ID.me role constant — mirrors @veterans/types TIER_2_IDME_ROLE. */
const TIER_2_IDME_ROLE = 'vet_app_verified'

/**
 * Build the veterans.pro ID.me verify URL, encoding the current shop URL as
 * returnTo so the user lands back here after verification.
 */
function buildVerifyUrl(): string {
  const returnTo = typeof window !== 'undefined' ? window.location.href : 'https://shop.veterans.pro'
  const base = 'https://veterans.pro/onboarding/verify/idme'
  return `${base}?returnTo=${encodeURIComponent(returnTo)}`
}

// ─── Pill / badge primitives ──────────────────────────────────────────────────

interface VerifiedPillProps {
  compact?: boolean
}

function VerifiedPill({ compact }: VerifiedPillProps) {
  if (compact) {
    return (
      <span
        role="status"
        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400"
        aria-label="Veteran verified — 10% discount applied at checkout"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10.293 2.293a1 1 0 011.414 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L5 7.586l5.293-5.293z"
            clipRule="evenodd"
          />
        </svg>
        Veteran verified — 10% off at checkout
      </span>
    )
  }

  return (
    <div
      role="status"
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-emerald-500/40 bg-emerald-950/30"
      aria-label="Veteran verified — 10% discount applied at checkout"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="text-emerald-400 flex-shrink-0"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
      <div>
        <p className="text-emerald-300 font-semibold text-sm">
          Veteran verified — 10% discount applied at checkout
        </p>
        <p className="text-emerald-400/70 text-xs mt-0.5">
          Your ID.me verification is active on this account.
        </p>
      </div>
    </div>
  )
}

interface LiveCtaProps {
  compact?: boolean
}

function LiveCta({ compact }: LiveCtaProps) {
  const verifyUrl = buildVerifyUrl()

  if (compact) {
    return (
      <a
        href={verifyUrl}
        className="inline-flex items-center gap-1 text-xs font-semibold text-gold hover:text-gold-bright transition-colors underline underline-offset-2"
        aria-label="Verify with ID.me to get 10% off — opens veterans.pro"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M6 1a5 5 0 100 10A5 5 0 006 1zm0 2v4m0 0l-1.5-1.5M6 7l1.5-1.5" />
        </svg>
        Veterans save 10% — verify with ID.me
      </a>
    )
  }

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-gold/30 bg-gold/5">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-gold flex-shrink-0 mt-0.5"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm">
          Veterans &amp; Active Duty: save 10% on every order
        </p>
        <p className="text-white/60 text-xs mt-0.5 mb-2">
          Verify your service with ID.me and your discount applies automatically at checkout.
        </p>
        <a
          href={verifyUrl}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gold text-white text-xs font-bold hover:bg-gold-bright transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          aria-label="Verify with ID.me to unlock your 10% veteran discount — opens veterans.pro"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7 0a7 7 0 100 14A7 7 0 007 0zm3.25 5.5a.75.75 0 00-1.5 0v2.19L7.53 6.47a.75.75 0 00-1.06 1.06l2 2a.75.75 0 001.06 0l2-2A.75.75 0 0010.25 6.47l-1.22 1.22V5.5h1.22z"
              clipRule="evenodd"
            />
          </svg>
          Verify with ID.me
        </a>
      </div>
    </div>
  )
}

// ─── Public component ─────────────────────────────────────────────────────────

export interface VetDiscountBannerProps {
  /**
   * compact — renders a single-line inline note.
   * Use on ProductDetail near the price; omit (full banner) for Cart and Checkout.
   */
  compact?: boolean
}

/**
 * VetDiscountBanner — feature-flagged ID.me discount affordance.
 *
 * Mount full (compact omitted) at the top of CartPage and CheckoutPage.
 * Mount compact on ProductDetail near the price line.
 */
export function VetDiscountBanner({ compact = false }: VetDiscountBannerProps) {
  const { session, loading } = useAuth()

  // Avoid flash of wrong state while session loads (only matters on full banner)
  if (loading && !compact) return null

  const isVerified = session?.role === TIER_2_IDME_ROLE

  if (isVerified) {
    return <VerifiedPill compact={compact} />
  }

  if (IDME_ENABLED) {
    return <LiveCta compact={compact} />
  }

  // Flag off: render nothing. Advertising a 10% discount that cannot be
  // obtained, on the cart and checkout pages, gives a buyer a reason to
  // abandon and "check back soon" for a perk with no date attached. The CTA
  // returns on its own the moment IDME_ENABLED flips true.
  return null
}
